import { Buyer } from '@40seas/entities/buyers';
import { Charge } from '@40seas/entities/charges';
import { EnumSchema, EntityReference, EntityReferenceSchema, Executor, FromSchema } from '@40seas/entities/common';
import { PaymentMethod, PaymentMethodProviderType } from '@40seas/entities/payment-methods';
import { Seller } from '@40seas/entities/sellers';
import { BaseEntity, Currency, WithBaseEntitySchema } from '@40seas/models';
import { z } from 'zod';
import { FundsLocation } from './funds.location.enum';
import { FundsStatus } from './funds.status.enum';
import { FundsUnmatchedReason } from './funds.unmatched-reason.enum';
import {
    BuyerMatchingReference,
    BuyerMatchingReferenceSchema,
    FundsMatchingDetails,
    FundsMatchingDetailsSchema,
} from './funds.matching-details';
import { FundsMatchingMethod } from './funds.matching-method.enum';

export const FundsSchema = WithBaseEntitySchema(
    z.object({
        buyer: z.string().or(Buyer['schema']).optional(),
        amount: z.number(),
        available: z.number().optional(),
        paymentMethod: z.string().or(PaymentMethod['schema']).optional(),
        providerId: z.string().nullable().optional(),
        providerType: EnumSchema(PaymentMethodProviderType).optional(),
        providerAccountId: z.string().optional(),
        currency: EnumSchema(Currency),
        status: EnumSchema(FundsStatus),
        location: EnumSchema(FundsLocation).optional(),
        reference: z.string().or(z.any()).optional(),
        payerName: z.string().or(z.any()).optional(),
        charges: z.array(z.string().or(z.any())).optional(),
        suggestedMatch: EntityReferenceSchema.optional(),
        suggestedBuyers: z.array(BuyerMatchingReferenceSchema).optional(),
        matchingMethod: EnumSchema(FundsMatchingMethod).optional(),
        matchingDetails: FundsMatchingDetailsSchema.optional(),
        unmatchedReason: EnumSchema(FundsUnmatchedReason).optional(),
    }),
).strict();

export class Funds extends FromSchema(FundsSchema) implements BaseEntity {
    id: string;
    buyer?: string | Buyer;
    amount: number;
    available?: number;
    paymentMethod?: string | PaymentMethod;
    providerId?: string;
    providerType?: PaymentMethodProviderType;
    providerAccountId?: string;
    currency: Currency;
    status: FundsStatus;
    location?: FundsLocation;
    reference?: string;
    payerName?: string;
    charges?: string[] | Charge[];
    suggestedMatch?: EntityReference;
    suggestedBuyers?: BuyerMatchingReference[];
    matchingMethod?: FundsMatchingMethod;
    matchingDetails?: FundsMatchingDetails;
    unmatchedReason?: FundsUnmatchedReason;
    createdAt: Date;
    updatedAt: Date;
    seller: string | Seller;
    createdBy?: Executor;
    tenantId: string;
    metadata?: { [key: string]: string };
    timeline?: { [status: string]: { updatedAt: Date } };
}

export class MultiTenantFunds extends Funds {}
